import type { HostedAgentSession, HostedAgentSessionVisibility } from "@/api/client/hosted-agents"

import { isActiveAgentRunStatus } from "@/features/ai-explorer/hooks/use-agent-runs-query"

/**
 * B6: visibility is stamped on the session at creation and never changed
 * retroactively. Sessions from before the stamp existed have no `visibility`
 * — treat them as personal (fail-closed).
 */
export function sessionVisibility(session: HostedAgentSession): HostedAgentSessionVisibility {
  return session.visibility === "open" ? "open" : "personal"
}

/**
 * Splits an agent's thread list on the stamped per-session `visibility` (B6).
 * The UI renders these as separate groups; an agent whose `memory_mode`
 * changed later can legitimately own threads of both kinds.
 */
export function partitionAgentSessions(sessions: HostedAgentSession[]): {
  open: HostedAgentSession[]
  personal: HostedAgentSession[]
} {
  const open: HostedAgentSession[] = []
  const personal: HostedAgentSession[] = []
  for (const session of sessions) {
    if (sessionVisibility(session) === "open") open.push(session)
    else personal.push(session)
  }
  return { open, personal }
}

/**
 * A thread listed via the per-agent endpoint that the caller does NOT own:
 * the backend stamps `currentUserRole: "owner"` on own threads and
 * `"reader"` on other users' open-stamped threads (B6 — readable by the
 * audience, writable by the owner only). Anything but an explicit owner
 * role is treated as foreign, fail-closed.
 */
export function isForeignSession(session: HostedAgentSession): boolean {
  return session.currentUserRole !== "owner"
}

/** Whether the session's stamped background agent run is still in flight. */
export function isSessionRunActive(session: HostedAgentSession): boolean {
  return isActiveAgentRunStatus(session.agent_run?.status)
}

export type SessionDateBucket = "today" | "past7Days" | "past30Days" | "older"

/**
 * Same date buckets as `useChatHistory`'s sidebar grouping (today / past 7
 * days / past 30 days / older), keyed on the session's `created` timestamp.
 */
export function sessionDateBucket(session: HostedAgentSession, now: Date): SessionDateBucket {
  if (!session.created) return "older"
  const created = new Date(session.created)
  if (Number.isNaN(created.getTime())) return "older"
  created.setHours(0, 0, 0, 0)

  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(today.getDate() - 7)
  const thirtyDaysAgo = new Date(today)
  thirtyDaysAgo.setDate(today.getDate() - 30)

  if (created.getTime() === today.getTime()) return "today"
  if (created >= sevenDaysAgo) return "past7Days"
  if (created >= thirtyDaysAgo) return "past30Days"
  return "older"
}

/** Buckets a (visibility-partitioned) thread group by date, dropping empty buckets. */
export function groupSessionsByDate(
  sessions: HostedAgentSession[],
  now: Date = new Date()
): { bucket: SessionDateBucket; sessions: HostedAgentSession[] }[] {
  const buckets: Record<SessionDateBucket, HostedAgentSession[]> = {
    today: [],
    past7Days: [],
    past30Days: [],
    older: [],
  }
  for (const session of sessions) {
    buckets[sessionDateBucket(session, now)].push(session)
  }
  return (Object.keys(buckets) as SessionDateBucket[])
    .filter((bucket) => buckets[bucket].length > 0)
    .map((bucket) => ({ bucket, sessions: buckets[bucket] }))
}

// -----------------------------------------------------------------------------
// Workspace polish (#3165): thread titles, rail section styling, status tones.
// -----------------------------------------------------------------------------

/** Header/rail thread titles are clipped here so one line never wraps. */
export const AGENT_THREAD_TITLE_MAX_LENGTH = 60

/**
 * A readable thread title derived from the first user message, for threads
 * the backend has not titled yet. Collapses whitespace, keeps the first
 * line, and clips on a word boundary so the ellipsis never cuts mid-word.
 * Never shows placeholder prose — an empty string means "no title yet" and
 * the caller decides what to render instead.
 */
export function agentThreadTitleFromMessage(
  message: string | null | undefined,
  maxLength: number = AGENT_THREAD_TITLE_MAX_LENGTH
): string {
  const normalized = (message ?? "").replace(/\s+/gu, " ").trim()
  if (!normalized || maxLength <= 0) return ""
  if (normalized.length <= maxLength) return normalized
  const clipped = normalized.slice(0, maxLength)
  const lastSpace = clipped.lastIndexOf(" ")
  const stem = lastSpace > Math.floor(maxLength * 0.6) ? clipped.slice(0, lastSpace) : clipped
  const trimmed = stem.replace(/[\s.,;:!?–—-]+$/u, "")
  return `${trimmed || clipped}…`
}

/**
 * The title to show for one thread: the stamped session title when the
 * backend set one, otherwise a client-side title derived from the thread's
 * first user message. Shared by the header subtitle and the rail rows (and
 * reusable by the chat view, which is the only place that holds messages).
 */
export function agentThreadTitle(
  session: HostedAgentSession | undefined,
  firstUserMessage?: string | null,
  maxLength: number = AGENT_THREAD_TITLE_MAX_LENGTH
): string {
  const stored = session?.title?.trim()
  if (stored) return agentThreadTitleFromMessage(stored, maxLength)
  return agentThreadTitleFromMessage(firstUserMessage, maxLength)
}

/**
 * Display name of a shared thread's owner, when the backend sent the
 * participant list. Foreign open threads often arrive without it — the rail
 * then shows the "Shared" marker alone rather than inventing a name.
 */
export function agentSessionOwnerName(session: HostedAgentSession): string | undefined {
  const participants = session.participants ?? []
  const owner =
    participants.find((participant) => participant.isOriginalOwner) ??
    participants.find((participant) => participant.role === "owner")
  return owner?.displayName.trim() || undefined
}

/**
 * One rail list, ordered the way the two old groups were stacked: threads
 * stamped `open` (B6) first, then personal ones. The per-row marker carries
 * what the removed "Shared threads" heading used to say.
 */
export function orderAgentSessions(sessions: HostedAgentSession[]): HostedAgentSession[] {
  const { open, personal } = partitionAgentSessions(sessions)
  return [...open, ...personal]
}

/** Client-side rail filter over the visible title and the owner's name. */
export function filterAgentSessions(
  sessions: HostedAgentSession[],
  query: string
): HostedAgentSession[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return sessions
  return sessions.filter((session) => {
    const owner = agentSessionOwnerName(session) ?? ""
    return `${agentThreadTitle(session)} ${owner}`.toLowerCase().includes(needle)
  })
}

/** Status tone of a dot/marker: fine, needs attention, or nothing claimed. */
export type AgentStatusTone = "ok" | "attention" | "problem" | "neutral"

export const AGENT_STATUS_DOT_CLASS: Record<AgentStatusTone, string> = {
  ok: "bg-success",
  attention: "bg-warning",
  problem: "bg-destructive",
  neutral: "bg-muted-foreground/50",
}

/**
 * Microsoft 365 connection status → dot tone. An unknown/unloaded status is
 * `neutral`: the pill must never claim a connection it has not seen.
 */
export function m365ConnectionTone(status: string | undefined): AgentStatusTone {
  switch (status) {
    case "connected":
      return "ok"
    case "reconnect_required":
    case "insufficient_scopes":
      return "attention"
    case "error":
      return "problem"
    default:
      return "neutral"
  }
}

/** Delivery receipt state → dot tone (provider acceptance is not proof of receipt). */
export function deliveryStateTone(state: string | undefined): AgentStatusTone {
  switch (state) {
    case "provider_accepted":
      return "ok"
    case "uncertain":
      return "attention"
    case "failed":
      return "problem"
    default:
      return "neutral"
  }
}

// One section style for the whole rail (#3165): a micro uppercase label in
// the display face, an optional right-hand count/action, 24 px between
// sections. Kept here so the four sections and the mobile sheet cannot drift.
export const AGENT_RAIL_SECTION_CLASS = "flex flex-col gap-2"
export const AGENT_RAIL_HEADER_CLASS = "flex min-h-6 items-center justify-between gap-2 px-1"
export const AGENT_RAIL_LABEL_CLASS =
  "font-display text-muted-foreground text-[11px] leading-none tracking-wider uppercase"
export const AGENT_RAIL_META_CLASS = "text-muted-foreground text-[11px]"
